/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed, get } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MilestoneType from '@linc-technologies/linced-common/enums/milestone-type';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import {
  findBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class MilestonesController extends Controller {
  queryParams = [{ selectedTab: { as: 'tab' } }];

  // Services
  @service
  fieldOptions;

  @service
  options;

  @service
  paperToaster;

  @service
  session;

  @service
  store;

  Jurisdiction = Jurisdiction;
  ProgressionJudgement = ProgressionJudgement;

  @tracked
  selectedTab = 'expectations';

  @action
  setSelectedTab(value) {
    this.selectedTab = value;
  }

  @tracked
  activeSubject;

  @action
  setActiveSubject(value) {
    this.activeSubject = value;
  }

  @alias('session.school')
  school;

  @optionAlias('hero:lock:milestones')
  milestonesLocked;

  @alias('model.subjects')
  buttons;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('model.milestoneSets.@each.isDefault')
  get hasMilestoneSets() {
    return (
      this.model.milestoneSets.filter((item) => !item.isDefault).length > 0
    );
  }

  @alias('model.milestoneSets')
  milestoneSetOptions;

  @tracked
  selectedMilestoneSet = null;

  @action
  setSelectedMilestoneSet(value) {
    this.selectedMilestoneSet = value;
  }

  @tracked
  milestoneSetDialogOpen = false;

  @tracked
  milestoneSet = null;

  @tracked
  milestoneSetLabel = '';

  @action
  setMilestoneSetLabel(value) {
    this.milestoneSetLabel = value;
  }

  @tracked
  milestoneSetSaveInProgress = false;

  @computed(
    'selectedMilestoneSet.{id,milestones.[]}',
    'model.{milestones.[],milestoneSets.@each.label}',
  )
  get milestones() {
    if (isEmpty(this.get('selectedMilestoneSet.id'))) {
      // No Milestone Set / default milestones selected.
      return this.model.milestones;
    }
    return this.get('selectedMilestoneSet.milestones');
  }

  milestoneTypes = MilestoneType.values;

  @tracked
  milestoneDialogOpen = false;

  @computed('activeSubject.id', 'model.goalCategories.[]')
  get subjectCategories() {
    let subjectId = this.get('activeSubject.id');
    if (isEmpty(subjectId)) {
      return [];
    } else {
      return this.get('model.goalCategories')
        .filter((category) => category.subject_id === subjectId)
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    }
  }

  @computed('activeSubject.id', 'model.levels.[]')
  get subjectLevels() {
    let subjectId = this.get('activeSubject.id');
    if (isEmpty(subjectId)) {
      return [];
    }

    return this.get('model.levels')
      .filter((level) => get(level, 'goalSubject.id') === subjectId)
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('activeSubject.id', 'model.expectations.[]')
  get subjectExpectations() {
    let subjectId = this.get('activeSubject.id');
    return this.get('model.expectations').filter(
      (expectation) => expectation.goal_subject_id === subjectId,
    );
  }

  unsetMarkers = [
    { sequence: 1, label: 'below' },
    { sequence: 2, label: 'at' },
    { sequence: 3, label: 'above' },
    { sequence: 4, label: 'wellAbove' },
  ];

  @computed(
    'activeSubject.{id,milestones.[]}',
    'model.{levels.[],expectations.[]}',
    'modificationCount',
  )
  get markers() {
    let milestones = this.get('activeSubject.milestones');
    let markers = [];
    let unsetMarkers = this.unsetMarkers;
    let numLevels = this.get('subjectLevels.length');
    let subjectExpectations = this.subjectExpectations;

    milestones.forEach((milestone) => {
      let milestoneMarkers = [];
      milestoneMarkers.__milestoneId__ = milestone.id;

      for (let i = -1; i < numLevels; i++) {
        let markerList = [];
        markerList.__pos__ = i;
        markerList.__milestoneId__ = milestone.id;
        milestoneMarkers.push(markerList);
      }

      let expectation = subjectExpectations.find(
        (item) => item.milestone_id === milestone.id,
      );
      if (!isEmpty(expectation)) {
        unsetMarkers.forEach((marker) => {
          let index = expectation.get(`${marker.label}Index`);
          if (index >= numLevels) {
            // Number of levels has changed.  We're going to have to adjust this to the last spot.
            index = numLevels - 1;
          }
          milestoneMarkers.find((item) => item.__pos__ === index).push(marker);
        });
      } else {
        let markerList = milestoneMarkers[0];
        unsetMarkers.forEach((marker) => markerList.push(marker));
      }

      markers.push(milestoneMarkers);
    });

    return markers;
  }

  @tracked
  levelDialogOpen = false;

  // modificationCount can be changed to trigger chip classes to be recalculated.
  @tracked modificationCount = 0;

  // Locking of milestones/expectations
  @tracked
  lockMilestonesDialogOpen = false;

  @tracked
  lockMilestonesFailed = false;

  @tracked
  lockMilestonesInProgress = false;

  @tracked
  deleteDialogOpen;

  @tracked
  deleteInProgress;

  @tracked
  deleteLabel;

  @tracked
  deleteType;

  @tracked
  errorMessage;

  @tracked
  levelBriefLabel;

  @action
  setLevelBriefLabel(value) {
    this.levelBriefLabel = value;
  }

  @tracked
  levelGoalCategory;

  @action
  setLevelGoalCategory(value) {
    this.levelGoalCategory = value;
  }

  @tracked
  levelId;

  @tracked
  levelLabel;

  @action
  setLevelLabel(value) {
    this.levelLabel = value;
  }

  @tracked
  levelSaveInProgress;

  @tracked
  milestoneDay;

  @action
  setMilestoneDay(value) {
    this.milestoneDay = value;
  }

  @tracked
  milestoneDuration;

  @action
  setMilestoneDuration(value) {
    this.milestoneDuration = value;
  }

  @tracked
  milestoneId;

  @tracked
  milestoneIsInterim;

  @action
  setMilestoneIsInterim(value) {
    this.milestoneIsInterim = value;
  }

  @tracked
  milestoneLabel;

  @action
  setMilestoneLabel(value) {
    this.milestoneLabel = value;
  }

  @tracked
  milestoneMonth;

  @action
  setMilestoneMonth(value) {
    this.milestoneMonth = value;
  }

  @tracked
  milestoneType;

  @action
  setMilestoneType(value) {
    this.milestoneType = value;
  }

  @tracked
  milestoneYearLevel;

  @action
  setMilestoneYearLevel(value) {
    this.milestoneYearLevel = value;
  }

  @tracked
  saveInProgress;

  @computed('milestoneMonth')
  get maxDaysInMonth() {
    // Hardcoded to non-leap year for avoiding invalid date 29-Feb for a non-leap year
    return moment(this.milestoneMonth + '-2001', 'MM-YYYY').daysInMonth();
  }

  @computed('fieldOptions.yearLevelOptions.[]')
  get yearLevelMap() {
    if (!isEmpty(this.fieldOptions.yearLevelOptions)) {
      return this.fieldOptions.yearLevelOptions[0].options;
    }
  }

  @action
  closeMilestoneSetDialog() {
    this.milestoneSet = null;
    this.milestoneSetLabel = '';
    this.milestoneSetDialogOpen = false;
    this.milestoneSetSaveInProgress = false;
  }

  @action
  editMilestoneSet(milestoneSet) {
    this.milestoneSet = milestoneSet;
    this.milestoneSetLabel = milestoneSet.label;
    this.milestoneSetDialogOpen = true;
  }

  @action
  addMilestoneSet() {
    this.milestoneSet = null;
    this.milestoneSetLabel = '';
    this.milestoneSetDialogOpen = true;
  }

  @action
  saveMilestoneSet() {
    this.milestoneSetSaveInProgress = true;
    let promise = Promise.resolve();
    if (isEmpty(this.milestoneSet)) {
      let record = this.store.createRecord('milestones/milestone-set', {
        schoolId: this.session.school,
        label: this.milestoneSetLabel,
      });
      promise = record.save().then((newSet) => (this.milestoneSet = newSet));
    } else if (isEmpty(this.milestoneSet.id)) {
      // Converting the default milestones into a named Set.
      let record = this.store.createRecord('milestones/milestone-set', {
        schoolId: this.session.school,
        label: this.milestoneSetLabel,
      });
      promise = record
        .save()
        .then((newSet) => {
          // Store the new Milestone Set for later steps.
          this.milestoneSet = newSet;

          // Move all default milestones to the new Set.
          return this.milestones.reduce((promiseChain, milestone) => {
            milestone.set('setId', newSet);
            return promiseChain.then(() => milestone.save());
          }, Promise.resolve());
        })
        .then(() => {
          // Update any Subjects which were using the default milestones,
          // and are either trackable or have existing Expectations.
          let subjectIdsWithExpectations = [
            ...new Set(
              this.model.expectations.map(
                (expectation) => expectation.goal_subject_id,
              ),
            ),
          ];
          return this.model.allSubjects
            .filter(
              (subject) =>
                subject.trackable ||
                subjectIdsWithExpectations.includes(subject.id),
            )
            .reduce((promiseChain, subject) => {
              if (isEmpty(subject.milestone_set_id)) {
                // Uses the default milestones.  Update it.
                subject.set('milestoneSetId', this.milestoneSet);
                return promiseChain.then(() => subject.save());
              }

              // Already uses a different Milestone Set; leave it unchanged.
              return promiseChain;
            }, Promise.resolve());
        })
        .then(() => {
          // Remove "Default Milestones" from the list of options.
          this.milestoneSetOptions = this.milestoneSetOptions.filter(
            (item) => !item.isDefault,
          );
        })
        .catch(() => {
          // Something failed.  Revert all changes and delete the new Set.
          // Revert milestones to be the default milestones.
          let revertChain = resolved(this.milestoneSet.milestones).reduce(
            (promiseChain, milestone) => {
              milestone.set('setId', null);
              return promiseChain.then(() => milestone.save());
            },
            Promise.resolve(),
          );

          // Revert subjects to the default milestones.
          revertChain = this.model.subjects.reduce((promiseChain, subject) => {
            if (subject.milestone_set_id === this.milestoneSet.id) {
              subject.set('milestoneSetId', null);
              return promiseChain.then(() => subject.save());
            }

            // Uses a different Milestone Set; leave it unchanged.
            return promiseChain;
          }, revertChain);

          // Delete the Set that was created.
          revertChain = revertChain.then(() => {
            this.milestoneSet.destroyRecord();
            this.milestoneSet = null;
          });

          // Reject so that the generic `catch()` below triggers a toast.
          return revertChain.finally(() => Promise.reject());
        });
    } else {
      this.milestoneSet.set('label', this.milestoneSetLabel);
      promise = this.milestoneSet.save();
    }
    promise
      .then(() => {
        let options = this.milestoneSetOptions;
        if (isEmpty(options.find((item) => item.id === this.milestoneSet.id))) {
          // New Milestone Set added.
          // Push it into the list of options and select it.
          options = [...options, this.milestoneSet];
          this.selectedMilestoneSet = this.milestoneSet;
        }
        // Sort the Milestone Sets alphabetically.  Initial sort by schoolId
        // ensures that the "Default Milestones" always comes first (if it
        // exists), as it has no schoolId.
        this.milestoneSetOptions = options
          .slice()
          .sort(
            (a, b) =>
              (a.school_id || '').localeCompare(b.school_id || '') ||
              (a.label || '').localeCompare(b.label || ''),
          );
        this.closeMilestoneSetDialog();
      })
      .catch(() => {
        this.toaster.error('Error saving Milestone Set!');
      })
      .finally(() => {
        this.milestoneSetSaveInProgress = false;
      });
  }

  @action
  setYearLevel(yearLevel) {
    this.milestoneYearLevel = yearLevel.valueInt;
  }

  @action
  addMilestone() {
    this.resetMilestoneForm();
    this.milestoneDialogOpen = true;
  }

  @action
  editMilestone(milestone) {
    this.milestoneId = milestone.get('id');
    this.milestoneLabel = milestone.get('label');
    this.milestoneType = this.milestoneTypes.find(
      (item) => item.value === milestone.get('type'),
    );
    this.milestoneDuration = milestone.get('duration');
    this.milestoneYearLevel = milestone.get('yearLevel');
    this.milestoneMonth = milestone.get('month');
    this.milestoneDay = milestone.get('day');
    this.milestoneIsInterim = milestone.get('isInterim');
    this.milestoneDialogOpen = true;
  }

  @action
  closeMilestoneDialog() {
    this.milestoneDialogOpen = false;
  }

  @action
  saveMilestone() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;

    if (isEmpty(this.milestoneId)) {
      promise = new Promise((resolve) => {
        let progressionType = ProgressionType.PROGRESSION;
        let record = this.store.createRecord('milestones/milestone', {
          schoolId: this.get('session.school'),
          setId: this.selectedMilestoneSet,
          label: this.milestoneLabel,
          sequence: this.get('milestones.length') + 1,
          type: this.get('milestoneType.value'),
          duration: this.milestoneDuration,
          yearLevel: this.milestoneYearLevel,
          month: this.milestoneMonth,
          day: this.milestoneDay,
          isInterim: this.milestoneIsInterim,
          progressionType,
        });

        resolve(record);
      });
    } else {
      promise = new Promise((resolve) => {
        const id = this.milestoneId;
        this.store.findRecord('milestones/milestone', id).then((milestone) => {
          milestone.set('label', this.milestoneLabel);
          milestone.set('type', this.get('milestoneType.value'));
          milestone.set('duration', this.milestoneDuration);
          milestone.set('yearLevel', this.milestoneYearLevel);
          milestone.set('month', this.milestoneMonth);
          milestone.set('day', this.milestoneDay);
          milestone.set('isInterim', this.milestoneIsInterim);

          resolve(milestone);
        });
      });
    }

    promise.then((record) => {
      let toaster = this.paperToaster;

      return record
        .save()
        .then(() => {
          this.milestoneDialogOpen = false;
          this.resetMilestoneForm();
          toaster.show('Milestone saved successfully.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
          this.get('model.milestones').update();
        })
        .catch(() => {
          record.rollbackAttributes();
          toaster.show('Error saving Milestone.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteMilestone(milestone) {
    this.errorMessage = '';
    this.deleteLabel = milestone.get('label');
    this.milestoneId = milestone.id;
    this.deleteType = 'Milestone';
    this.deleteDialogOpen = true;
  }

  @action
  addLevel() {
    this.resetLevelForm();
    this.levelDialogOpen = true;
  }

  @action
  async editLevel(level) {
    this.levelId = level.get('id');
    this.levelLabel = level.get('label');
    this.levelBriefLabel = level.get('briefLabel');
    // A belongsTo takes a record, not the async proxy.
    this.levelGoalCategory = await level.goalCategory;
    this.levelDialogOpen = true;
  }

  @action
  closeLevelDialog() {
    this.levelDialogOpen = false;
  }

  @action
  saveLevel() {
    this.levelSaveInProgress = true;
    this.errorMessage = '';
    let promise;

    if (isEmpty(this.levelId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('milestones/level', {
          schoolId: this.get('session.school'),
          label: this.levelLabel,
          briefLabel: this.levelBriefLabel,
          sequence: this.get('subjectLevels.length') + 1,
          goalCategoryId: this.levelGoalCategory,
          progressionType: ProgressionType.PROGRESSION,
          goalSubjectId: this.activeSubject,
        });

        resolve(record);
      });
    } else {
      promise = new Promise((resolve) => {
        const id = this.levelId;
        this.store.findRecord('milestones/level', id).then((level) => {
          level.set('label', this.levelLabel);
          level.set('briefLabel', this.levelBriefLabel);
          level.set('goalCategoryId', this.levelGoalCategory);

          resolve(level);
        });
      });
    }

    promise.then((record) => {
      let toaster = this.paperToaster;

      return record
        .save()
        .then(() => {
          this.levelDialogOpen = false;
          this.resetLevelForm();
          toaster.show('Level saved successfully.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
          this.get('model.levels').update();
        })
        .catch(() => {
          // rollbackAttributes() alone would leave goalCategoryId.
          rollbackRecord(record);
          toaster.show('Error saving Level.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        })
        .then(() => {
          this.levelSaveInProgress = false;
        });
    });
  }

  @action
  deleteLevel(level) {
    this.errorMessage = '';
    this.deleteLabel = level.get('label');
    this.levelId = level.id;
    this.deleteType = 'Level';
    this.deleteDialogOpen = true;
  }

  // Generic delete handler for all entity types
  @action
  confirmDelete(confirmed) {
    let toaster = this.paperToaster;
    let type = this.deleteType;
    let typeLower = type.toLowerCase();

    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let entity = this.store.peekRecord(
          `milestones/${typeLower}`,
          this.get(`${typeLower}Id`),
        );

        // Delete record (locally + pushed to server).
        return entity
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            entity.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteDialogOpen = false;
        this[`reset${type}Form`]();
        if (confirmed) {
          toaster.show(`${type} deleted successfully.`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
        }
      })
      .catch(() => {
        toaster.show(`Error deleting ${type}.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      entity.save();
    });
  }

  // drag-drop sorting for expectations
  @action
  setExpectation({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    let expectation = this.subjectExpectations.find(
      (item) => item.milestone_id === targetList.__milestoneId__,
    );
    if (isEmpty(expectation)) {
      expectation = this.store.createRecord('milestones/expectation', {
        schoolId: this.get('session.school'),
        milestoneId: findBy(
          this.get('activeSubject.milestones'),
          'id',
          targetList.__milestoneId__,
        ),

        // Set all indices to -1 - we'll adjust them (where applicable) before saving.
        belowIndex: -1,
        atIndex: -1,
        aboveIndex: -1,
        wellAboveIndex: -1,

        progressionType: ProgressionType.PROGRESSION,
        goalSubjectId: this.activeSubject,
      });
    }

    // Set new expectation level for dragged handle
    let newPos = targetList.__pos__;
    expectation.set(`${item.label}Index`, newPos);

    // If any higher levels are now below this one, bring them up
    let targets = this.unsetMarkers;
    let target = targets.indexOf(item);
    for (let i = 0; i < target; i++) {
      let key = `${targets[i].label}Index`;
      if (expectation.get(key) > newPos) {
        expectation.set(key, newPos);
      }
    }
    for (let i = target + 1; i < targets.length; i++) {
      let key = `${targets[i].label}Index`;
      if (expectation.get(key) < newPos) {
        expectation.set(key, newPos);
      }
    }

    // Save the updated expectation record
    expectation.save().then(() => {
      this.get('model.expectations')
        .update()
        .then(() => {
          this.incrementProperty('modificationCount');
        });
    });
  }

  @action
  calculateLevelClass(index, expectation) {
    if (isEmpty(expectation)) {
      return;
    }
    if (index <= expectation.get('belowIndex')) {
      return 'colours-progress--1';
    } else if (index <= expectation.get('atIndex')) {
      return 'colours-progress--2';
    } else if (index <= expectation.get('aboveIndex')) {
      return 'colours-progress--3';
    } else if (index <= expectation.get('wellAboveIndex')) {
      return 'colours-progress--4';
    } else {
      return 'colours-progress--5';
    }
  }

  @action
  lockMilestones() {
    this.lockMilestonesDialogOpen = true;
    this.lockMilestonesFailed = false;
    this.lockMilestonesInProgress = false;
  }

  @action
  confirmLockMilestones(lock) {
    if (lock) {
      this.lockMilestonesInProgress = true;
      this.school
        .reload()
        .then((school) => {
          let lockField = school.get('fields.hero:lock:milestones');
          if (isEmpty(lockField)) {
            // Field value doesn't exist in this school - add it.
            school.addField('hero:lock:milestones', DataType.BOOLEAN);
            lockField = school.get('fields.hero:lock:milestones');
          }
          lockField.set('value', true);
          return school.save();
        })
        .then(() => {
          this.lockMilestonesDialogOpen = false;
        })
        .catch(() => {
          this.lockMilestonesFailed = true;
        })
        .finally(() => {
          this.lockMilestonesInProgress = false;
        });
    } else {
      this.lockMilestonesDialogOpen = false;
    }
  }

  resetMilestoneForm() {
    this.milestoneId = null;
    this.milestoneLabel = '';
    this.milestoneType = null;
    this.milestoneDuration = 0;
    this.milestoneYearLevel = 0;
    this.milestoneMonth = 0;
    this.milestoneDay = 0;
    this.milestoneIsInterim = false;
  }

  resetLevelForm() {
    this.levelId = null;
    this.levelLabel = '';
    this.levelBriefLabel = '';
    this.levelGoalCategory = null;
  }
}
